import { IImgLoaderManager } from './interface';
import _get from 'lodash/get';
import _has from 'lodash/has';
import i18n from '@i18next/i18n';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import {
    NEXTPAGEHOVER,
    NEXTPAGENORMAL,
    PENHOVER,
    PENNORMAL,
    PREPAGEHOVER,
    PREPAGENORMAL,
    STOPHOVER,
    STOPNORMAL,
    TENCENTDOCS,
} from '@src/framework/player/src/img/pageindex';
import { convertSuffix } from './constant';

const highPriority = 1;
const lowPriority = 0;
const retryTimesKey = 'retryTimesKey';
const srcKey = 'srcKey';
const thumbKey = 'thumbKey';
const objsKey = 'objsKey';

const timeoutDuration = 200; // /100毫秒？

export class ImgLoaderManager implements IImgLoaderManager {
    public convertUrlSet: Set<string>;
    private elements: {
        highPriorityDict: {}; // {'url': [objs]}
        highPriorityArray: any[]; // [[objs]]
        lowPriorityDict: {}; // Dict方便查询
        lowPriorityArray: any[];
    };
    private actElements: any[];
    private loadedUrls: {};
    private property: string;
    private originalProperty: string;
    private loadingElements;
    private maxLoadNumber: number;
    private maxRetryTimes: number;
    private preLoadResourceTimer;
    private timer;

    public constructor() {
        this.elements = {
            highPriorityDict: {}, // {'url': [objs]}
            highPriorityArray: [], // [[objs]]
            lowPriorityDict: {}, // Dict方便查询
            lowPriorityArray: [], // Array方便记录顺序，  二者里面存同一个objs数组对象，增删的时候要保持两者同步
        };

        this.actElements = []; // dom树上的所有img标签的引用，方便进行memorycache管理
        this.loadedUrls = {};

        this.property = 'data-src';
        this.originalProperty = 'data-original';


        this.loadingElements = [];
        this.maxLoadNumber = 5;
        this.maxRetryTimes = 3;

        this.convertUrlSet = new Set();

        this.preLoadResourceTimer = setTimeout(() => {
            this.loadPlayIconInfo();
        }, 30000);
    }

    public setScrollTarget = (obj) => {
        obj.addEventListener('scroll', this.handlescroll);
    };

    // eslint-disable-next-line complexity
    public addImage = (obj) => {
        this.actElements.push(obj);

        const tempObj = obj;
        const url = obj.getAttribute(this.property);

        if (!url) {
            // 当 url 不存在时，如果原始地址为 blob，需要加载图片导入宽高
            const originUrl = obj.getAttribute('xlink:href');
            if (ImageUrl.isBlobUrl(originUrl)) {
                this.loadBlobImage(obj, originUrl);
            }
            return;
        }

        if (ImageUrl.isBlobUrl(url)) {
            this.changeHref(obj, url);
            this.loadBlobImage(obj, url);
            return;
        }

        for (let i = 0; i < this.loadingElements.length; i++) {
            // 判断是否已经在下载队列了。  如果是不触发下载直接等同url的img标签下载成功，一起复用渲染
            if (this.loadingElements[i][srcKey] === url) {
                this.loadingElements[i][objsKey].push(obj);
                return;
            }
        }

        const result = this.executor(obj);
        if (result.priority === highPriority) {
            let objs = this.elements.highPriorityDict[url];
            if (objs && objs.length > 0) {
                objs.push(obj); // 如果存在，则push到objs最后，因为dict和array里是同一份objs数据，所以array不需要处理
            } else {
                objs = [obj];
                this.elements.highPriorityDict[url] = objs;
                this.elements.highPriorityArray.push(objs);
            }
            tempObj.priority = highPriority;
        } else if (result.priority === lowPriority) {
            let objs = this.elements.lowPriorityDict[url];
            if (objs && objs.length > 0) {
                objs.push(obj);
            } else {
                objs = [obj];
                this.elements.lowPriorityDict[url] = objs;
                this.elements.lowPriorityArray.push(objs);
            }
            tempObj.priority = lowPriority;
        }

        this.handle();
    };

    public removeImage(obj) {
        const url = obj.getAttribute(this.property);
        if (obj.priority === highPriority) {
            this.handleHighPriorityRemoveImage(obj, url);
        } else if (obj.priority === lowPriority) {
            this.handleLowPriorityRemoveImage(obj, url);
        }

        let index = -1;
        for (let i = 0; i < this.actElements.length; i++) {
            if (obj === this.actElements[i]) {
                this.changeHref(obj, ''); // src置空，方便memorycache回收资源
                index = i;
                break;
            }
        }
        if (index !== -1) {
            this.actElements.splice(index, 1);
        }
    }

    public getUrlParam = (url, paraName) => {
        if (!url) {
            return '';
        }
        const arrObj = url.split('?');
        if (arrObj.length > 1) {
            const arrPara = arrObj[1].split('&');
            let arr;

            for (let i = 0; i < arrPara.length; i++) {
                arr = arrPara[i].split('=');
                if (arr !== null && arr[0] === paraName) {
                    return arr[1];
                }
            }
            return '';
        }
        return '';
    };

    /**
     * @param {*} url
     * @returns 0 为未下载或者下载未成功； 1， 表示下载的缩略图；  2, 表示下载的原图
     * @memberof imageLoaderManager
     */
    public checkIsLoaded = (url) => {
        if (_has(this.loadedUrls, this.getThumb1600(url))) {
            return 1;
        }

        if (_has(this.loadedUrls, url)) {
            return 2;
        }

        return 0;
    };

    public needDownloadElementsCount = () => {
        const count = this.elements.highPriorityArray.length + this.elements.lowPriorityArray.length;
        return count;
    };

    // 1600*900
    public getThumb1600 = (url) => {
        if (!this.checkNeedThumb(url)) {
            return url;
        }

        const array = url.split('?');
        if (array.length === 1) {
            return this.getThumbWithOutQuery(url);
        }
        if (array.length === 2) {
            return this.getThumbWithQuery(url);
        }
        return url;
    };

    private executor = (elem) => {
        const domClientRect = elem.getBoundingClientRect();
        if (domClientRect.y + domClientRect.height >= 0 && domClientRect.y <= window.innerHeight) {
            // /屏幕内优先加载
            return { obj: elem, priority: highPriority };
        }
        return { obj: elem, priority: lowPriority };
    };


    private loadBlobImage(obj, url) {
        const image = new Image();

        image.onload = () => {
            obj.callback?.(image.width, image.height);
        };

        image.onerror = function () {
            obj.onLoadError?.();
        };

        image.src = url;
    }

    private handleHighPriorityRemoveImage(obj, url) {
        const objs = this.elements.highPriorityDict[url];
        let index = -1;
        for (let i = 0; objs && i < objs.length; i++) {
            if (obj === objs[i]) {
                index = i;
                break;
            }
        }
        if (index !== -1) {
            objs.splice(index, 1);
        }
        if (this.objLengthZero(objs)) {
            delete this.elements.highPriorityDict[url];

            index = -1;
            for (let j = 0; j < this.elements.highPriorityArray.length; j++) {
                if (
                    this.elements.highPriorityArray[j].length > 0
                    && _get(obj, 'dataset.src') === this.elements.highPriorityArray[j][0].getAttribute(this.property)
                ) {
                    index = j;
                    break;
                }
            }
            if (index !== -1) {
                this.elements.highPriorityArray.splice(index, 1);
            }
        }
    }

    private objLengthZero = objs => objs && objs.length === 0;

    private handleLowPriorityRemoveImage(obj, url) {
        const objs = this.elements.lowPriorityDict[url];
        let index = -1;
        for (let i = 0; objs && i < objs.length; i++) {
            if (obj === objs[i]) {
                index = i;
                break;
            }
        }
        if (index !== -1) {
            this.elements.lowPriorityDict[url].splice(index, 1);
        }
        if (this.objLengthZero(objs)) {
            delete this.elements.lowPriorityDict[url];

            index = -1;
            for (let j = 0; j < this.elements.lowPriorityArray.length; j++) {
                if (
                    this.elements.lowPriorityArray[j].length > 0
                    && obj.dataset.src === this.elements.lowPriorityArray[j][0].getAttribute(this.property)
                ) {
                    index = j;
                    break;
                }
            }
            if (index !== -1) {
                this.elements.lowPriorityArray.splice(index, 1);
            }
        }
    }

    private updateImage = () => {
        // /暂时不做处理，方案还没想明白
    };

    /* 此处调整优先级 */
    // eslint-disable-next-line complexity
    private checkUnLoadElemPriority = () => {
        for (let i = this.elements.highPriorityArray.length - 1; i >= 0; i--) {
            const array = this.elements.highPriorityArray[i];
            const { inScreen, obj } = this.checkIsInScreen(array);
            if (!inScreen && obj && this.property) {
                // 本来高优， 被滚走了
                this.elements.lowPriorityArray.push(array);
                this.elements.lowPriorityDict[obj.getAttribute(this.property) ?? 0] = array;
                this.elements.highPriorityArray.splice(i, 1);
                delete this.elements.highPriorityDict[obj.getAttribute(this.property) ?? 0];
            }
        }

        for (let i = this.elements.lowPriorityArray.length - 1; i >= 0; i--) {
            const array = this.elements.lowPriorityArray[i];
            const { inScreen, obj } = this.checkIsInScreen(array);
            if (inScreen && obj && this.property) {
                // 本来低优， 被滚进来了
                this.elements.highPriorityArray.push(array);
                this.elements.highPriorityDict[obj.getAttribute(this.property) ?? 0] = array;
                this.elements.lowPriorityArray.splice(i, 1);
                delete this.elements.lowPriorityDict[obj.getAttribute(this.property) ?? 0];
            }
        }
    };

    private checkIsInScreen = (array: HTMLElement[]) => {
        let inScreen = false;
        let obj: HTMLElement | null = null;
        for (let j = 0; j < array.length; j++) {
            obj = array[j];
            const domClientRect = obj.getBoundingClientRect();
            if (domClientRect.y + domClientRect.height >= 0 && domClientRect.y <= window.innerHeight) {
                // /屏内
                inScreen = true;
            }
        }
        return { inScreen, obj };
    };

    private handlescroll = () => {
        if (this.timer !== null) {
            clearTimeout(this.timer);
        }

        this.timer = setTimeout(() => {
            this.checkUnLoadElemPriority();
            this.handle();
        }, timeoutDuration);
    };

    private isValidQQAdaptDomain(url) {
        if (!url || url.length < 1) {
            // /图片路径非法
            return false;
        }
        return url.indexOf(CUSTOM.CDN.QPIC) > 0;
    }

    private isValidDocimg8Domain(url) {
        if (!url || url.length < 1) {
            // /图片路径非法
            return false;
        }
        return url.indexOf(window.hostname) > 0;
    }

    private checkNeedThumb = (url) => {
        if (!this.isValidQQAdaptDomain(url) && !this.isValidDocimg8Domain(url)) {
            return false;
        }
        if (this.getUrlParam(url, '_type') === 'svg') {
            return false;
        }
        if (this.getUrlParam(url, 'scene') === 'player') {
            return false;
        }
        const array = url.split('?');
        if (!array || array.length > 2) {
            return false;
        }
        return true;
    };

    private getThumbWithOutQuery = (url) => {
        const array = url.split('?');
        if (array[0].length > 4) {
            const [str] = array;
            const subStr = str.substring(str.length - 4);
            if (subStr === '1600') {
                // 如果已经是缩略图路径了，  就不需要再转换了
                return url;
            }
        }
        if (this.isValidQQAdaptDomain(url)) {
            return `${url.substring(0, url.length - 1)}1600`;
        }
        return `${array[0]}/1600`;
    };

    private getThumbWithQuery = (url) => {
        const curScreenDPR = window.devicePixelRatio || 1;
        const curScreenInfo = window.screen;
        const curScreenWidth = curScreenInfo.width * curScreenDPR;
        const curScreenHeight = curScreenInfo.height * curScreenDPR;

        const array = url.split('?');
        const { width: picWidth, height: picHeight } = ImageUrl.getImageSizeFromUrl(url);
        if (isNaN(picWidth) || isNaN(picHeight)) {
            return url;
        }

        // w * h <= 屏幕宽高时，使用原图
        if (picWidth * picHeight <= curScreenWidth * curScreenHeight) {
            return url;
        }

        // 如果已经是缩略图路径了，就不需要再转换了
        if (array[0].length > 4) {
            const [str] = array;
            const subStr = str.substring(str.length - 4);
            if (subStr === '1600') {
                return url;
            }
        }

        // qqadapt 域名，url 不一致
        if (this.isValidQQAdaptDomain(url)) {
            return `${url.substring(0, array[0].length - 1)}1600?${array[1]}`;
        }
        return `${array[0]}/1600?${array[1]}`;
    };

    private onLoad = (keyValue, width, height, original) => {
        /* 给img标签设置下载完成的src */
        for (let i = 0; i < keyValue[objsKey].length; i++) {
            const obj = keyValue[objsKey][i];
            const url = this.getSrcUrl(keyValue, original);

            this.changeHref(obj, url);

            if (obj.callback) {
                obj.callback(width, height);
            }
        }

        if (!original && _has(keyValue, thumbKey)) {
            this.loadedUrls[keyValue[thumbKey]] = true;
        } else {
            this.loadedUrls[keyValue[srcKey]] = true;
        }

        /* 从正在下载队列里移除 */
        let index = -1;
        for (let i = 0; i < this.loadingElements.length; i++) {
            if (keyValue === this.loadingElements[i]) {
                index = i;
                break;
            }
        }

        if (index !== -1) {
            this.loadingElements.splice(index, 1);
        }

        /* 继续触发handle检查是否有需要下载的img */
        this.handle();
    };

    private handleLoadError = (keyValue) => {
        for (let i = 0; i < keyValue[objsKey].length; i++) {
            const obj = keyValue[objsKey][i];
            if (obj.onLoadError) {
                obj.onLoadError();
            }
        }

        let index = -1;
        for (let i = 0; i < this.loadingElements.length; i++) {
            if (keyValue === this.loadingElements[i]) {
                index = i;
                break;
            }
        }
        if (index !== -1) {
            this.loadingElements.splice(index, 1);
        }
        const url = keyValue[thumbKey];
        window.log.info(`${i18n.t(' [下载图片] ' + ' - ser返回fail [下载失败] - ')}${url}`);

        window.dispatchEvent(new Event('check-slide-render-state'));
    };

    private onError = (keyValue, original, needConvert) => {
        // /重试
        const tempKeyValue = keyValue;
        if (keyValue[retryTimesKey] < this.maxRetryTimes) {
            tempKeyValue[retryTimesKey] = keyValue[retryTimesKey] + 1;
            this.lazyLoad(keyValue);
        } else if (!original && keyValue[retryTimesKey] === this.maxRetryTimes) {
            // 非原图， 再试一次原图，  直接原图，就跳过
            tempKeyValue[retryTimesKey] = keyValue[retryTimesKey] + 1;
            this.lazyLoad(keyValue, true);
        } else if (!needConvert && keyValue[retryTimesKey] >= this.maxRetryTimes) {
            // 重试失败后转换成 png 格式再重试一次
            tempKeyValue[retryTimesKey] = keyValue[retryTimesKey] + 1;
            this.lazyLoad(keyValue, true, true);
        } else {
            // 重试次数到了，就移除出队列，并上报失败原因
            this.handleLoadError(keyValue);
        }
    };

    private handle = () => {
        let indexArray: number[] = [];
        for (
            let i = 0;
            i < this.elements.highPriorityArray.length && this.loadingElements.length < this.maxLoadNumber;
            i++
        ) {
            if (!this.elements.highPriorityArray[i][0]) {
                indexArray.push(i); // 记录下标
                continue;
            }
            const src = this.handleLoadImage(this.elements.highPriorityArray[i]);
            delete this.elements.highPriorityDict[src]; // 从等待字典里删除
            indexArray.push(i); // 记录下标
        }
        for (let i = indexArray.length - 1; i >= 0; i--) {
            this.elements.highPriorityArray.splice(indexArray[i], 1);
        }

        indexArray = [];
        for (
            let i = 0;
            i < this.elements.lowPriorityArray.length && this.loadingElements.length < this.maxLoadNumber;
            i++
        ) {
            if (!this.elements.lowPriorityArray[i][0]) {
                indexArray.push(i); // 记录下标
                continue;
            }
            const src = this.handleLoadImage(this.elements.lowPriorityArray[i]);
            delete this.elements.lowPriorityDict[src]; // 从等待字典里删除
            indexArray.push(i); // 记录下标
        }
        for (let i = indexArray.length - 1; i >= 0; i--) {
            this.elements.lowPriorityArray.splice(indexArray[i], 1);
        }
    };

    private handleLoadImage = (array) => {
        const src = array[0].getAttribute(this.property);
        const keyValue = {
            srcKey: src,
            objsKey: array,
            priorityKey: array[0].priority,
            retryTimesKey: 0,
        };

        const thumSrc = this.getThumb1600(keyValue.srcKey);
        if (thumSrc) {
            keyValue[thumbKey] = thumSrc;
        } else {
            keyValue[thumbKey] = src;
        }

        this.loadingElements.push(keyValue); // 添加到下载中队列
        const original = array[0].getAttribute(this.originalProperty);
        if (original) {
            this.lazyLoad(keyValue, true); // 触发下载
        } else {
            this.lazyLoad(keyValue); // 触发下载
        }
        return src;
    };

    private lazyLoad = (keyValue, original?, needConvert?) => {
        const oImg = new Image();

        const url = this.getRequestUrl(keyValue, original, needConvert);

        oImg.onload = () => {
            this.onLoad(keyValue, oImg.width, oImg.height, original);
        };

        oImg.onerror = () => {
            this.onError(keyValue, original, needConvert);
        };
        /**
         * 因为Canvas请求图片设置了crossOrigin，这里Dom请求也必须设置。
         * 否则当Dom请求并缓存后，Canvas拿到了Dom的缓存图片(未设置crossOrigin)，导致Canvas获取图片报错
         */
        oImg.crossOrigin = 'Anonymous';
        oImg.src = url;
        return oImg;
    };

    private getUrl(keyValue, original) {
        let url = keyValue[srcKey];
        if (!original && _has(keyValue, thumbKey)) {
            url = keyValue[thumbKey];
        }
        return url;
    }

    private getRequestUrl(keyValue, original, needConvert) {
        let url = this.getUrl(keyValue, original);

        // 兼容bug~http://tapd.oa.com/TIM_web/bugtrace/bugs/view/1010143861086744543
        // Safari不支持webp格式，添加兼容逻辑，重试两次没有成功，url添加格式转换后缀
        if (needConvert) {
            this.convertUrlSet.add(url);
            url = `${url}${convertSuffix}`;
        }
        return url;
    }

    private getSrcUrl(keyValue, original) {
        let url = this.getUrl(keyValue, original);

        if (this.convertUrlSet.has(url)) {
            url = `${url}${convertSuffix}`;
        }

        return url;
    }

    private changeHref = (obj, url) => {
        obj.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', url);
    };

    private loadPlayIconInfo = () => {
        if (window.isInWorker) return;
        const images = [
            NEXTPAGEHOVER,
            NEXTPAGENORMAL,
            PENHOVER,
            PENNORMAL,
            PREPAGEHOVER,
            PREPAGENORMAL,
            STOPHOVER,
            STOPNORMAL,
            TENCENTDOCS,
        ];
        images.forEach((element) => {
            const oImg = new Image();
            oImg.src = element;
        });
    };
}
